import { useState } from "react";
import { WorkToastApprovalBody } from "@/components/toasts/work-toast-approval-body";
import { decideApproval, type ApprovalDecision } from "@/lib/decide-approval";
import type { DirectApprovalStatus } from "@/lib/direct-approval-notice";
import { useDirectApprovalSettled } from "@/stores/direct-approval-settled-store";
import type { ConversationMessage } from "@/stores/conversation-message-types";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";

const SETTLED_KEY = {
  allowed: "approvalAllowed",
  denied: "approvalDenied",
  expired: "approvalExpired",
} as const;

/** A coding session's tool approval in the chat it was dispatched from.
 *  It answers through the same task endpoint as the task panel, so either
 *  place settles both; the outcome replaces the buttons. */
export function ConversationDirectApprovalCard({
  id,
  approval,
  testId = "conversation-frame",
}: {
  id: string;
  approval: NonNullable<ConversationMessage["directApproval"]>;
  testId?: string;
}) {
  const t = useT(framesConversationMessages);
  const live = useDirectApprovalSettled(approval.approvalId);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState("");
  // Kept after a successful answer, so a missed settle event never makes
  // the card answerable again.
  const [decided, setDecided] = useState<DirectApprovalStatus>();
  const status = live ?? decided ?? approval.status;
  const cardTestId = `${testId}-direct-approval-${id}`;

  async function decide(decision: ApprovalDecision) {
    setDeciding(true);
    setError("");
    try {
      await decideApproval(
        {
          projectId: approval.projectId,
          taskId: approval.taskId,
          approvalId: approval.approvalId,
        },
        decision,
      );
      setDecided(decision === "approve" ? "allowed" : "denied");
    } catch (err) {
      setError(
        err instanceof Error && err.message ? err.message : t("approvalFailed"),
      );
    } finally {
      setDeciding(false);
    }
  }

  return (
    <div
      className="my-2 flex flex-col gap-1.5 rounded-control border border-glass-stroke-faint bg-glass-fill px-3 py-2"
      data-testid={cardTestId}
      data-status={status}
    >
      {status === "pending" ? (
        <WorkToastApprovalBody
          approval={{
            approvalId: approval.approvalId,
            title: approval.title,
            description: approval.description,
            expiresAt: null,
          }}
          disabled={deciding}
          onDecide={(decision) => void decide(decision)}
        />
      ) : (
        <>
          <p className="text-appkit-body text-primary">{approval.title}</p>
          <p
            className="text-appkit-small text-muted"
            data-testid={`${cardTestId}-outcome`}
          >
            {t(SETTLED_KEY[status])}
          </p>
        </>
      )}
      {error ? (
        <p className="text-appkit-small text-muted" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
